import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MilkControl } from '@homeleite/domain';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { firstValueFrom } from 'rxjs';
import { EMPTY } from '@homeleite/common-utils';

function missingReasonText(value: unknown): string | null {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value && 'description' in value) {
        const description = (value as { description?: unknown }).description;
        return description ? String(description) : null;
    }
    return null;
}

@Injectable({
    providedIn: 'root'
})
export class PdfGeneratorService {
    private logoBase64: string | null = null;

    constructor(private http: HttpClient) {}

    public async generateMilkControlReport(milkControl: MilkControl): Promise<void> {
        const { fileName, pdf } = await this.createMilkControlReportPdf(milkControl);
        await pdf.download(fileName);
    }

    public async buildMilkControlReport(milkControl: MilkControl): Promise<{ fileName: string; blob: Blob }> {
        const { fileName, pdf } = await this.createMilkControlReportPdf(milkControl);
        const blob: Blob = await pdf.getBlob();
        return { fileName, blob };
    }

    private async createMilkControlReportPdf(milkControl: MilkControl): Promise<{ fileName: string; pdf: any }> {
        try {
            if (!this.logoBase64) {
                this.logoBase64 = await this.getBase64ImageFromURL(
                    'assets/images/default-homeleite.png'
                );
            }
        } catch (error) {
            console.error(error);
            this.logoBase64 = null;
        }

        const preparedData = this.groupAndPrepareData(milkControl);

        const reportDate = milkControl.finishedAt || milkControl.startedAt;
        const parsedReportDate = reportDate ? new Date(reportDate) : null;
        const formattedDateWithDash =
            parsedReportDate && !Number.isNaN(parsedReportDate.getTime())
                ? new Intl.DateTimeFormat('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                  })
                      .format(parsedReportDate)
                      .replace(/\//g, '-')
                : 'controle';

        const docDefinition: TDocumentDefinitions = {
            pageSize: 'A4',
            pageMargins: [40, 110, 40, 60],

            background: (currentPage) => {
                if (currentPage === 1) {
                    return this.buildHeader(milkControl);
                }
                return null;
            },

            content: [
                ...preparedData.lots.map((lotData) => this.buildLotSection(lotData)),
                this.buildGeneralSummary(preparedData)
            ],
            styles: {
                lotTitle: {
                    fontSize: 12,
                    bold: true,
                    margin: [0, 15, 0, 5],
                    alignment: 'center'
                },
                tableHeader: {
                    bold: true,
                    fontSize: 9,
                    color: 'black',
                    fillColor: '#dedede',
                    alignment: 'center'
                },
                summaryRowLabel: {
                    bold: true,
                    fontSize: 9,
                    alignment: 'left',
                    fillColor: '#f0f0f0'
                },
                summaryRowValue: {
                    bold: true,
                    fontSize: 9,
                    alignment: 'center',
                    fillColor: '#f0f0f0'
                },
                centerAlign: {
                    alignment: 'center'
                },
                summaryHeader: {
                    bold: true,
                    fontSize: 9,
                    alignment: 'center'
                },
                lotBoxTitle: {
                    fontSize: 12,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 5, 0, 5],
                    fillColor: '#dedede'
                },
                missingReason: {
                    fontSize: 9,
                    italics: true,
                    color: 'black',
                    alignment: 'center',
                    bold: true
                }
            },
            defaultStyle: {
                fontSize: 9,
                alignment: 'left'
            }
        };

        const pdfMakeModule = await import('pdfmake/build/pdfmake');
        const pdfFontsModule = await import('pdfmake/build/vfs_fonts');

        const pdfMake = (pdfMakeModule.default || pdfMakeModule) as any;
        const pdfFonts = (pdfFontsModule.default || pdfFontsModule) as any;

        pdfMake.addVirtualFileSystem(pdfFonts);

        const producerCode = milkControl.producer?.code || 'rebanho';
        return { fileName: `RCL_${producerCode}_${formattedDateWithDash}.pdf`, pdf: pdfMake.createPdf(docDefinition) };
    }

    private async getBase64ImageFromURL(url: string): Promise<string> {
        const blob = await firstValueFrom(this.http.get(url, { responseType: 'blob' }));
        // O nginx do admin devolve index.html (200) quando o arquivo não existe.
        // pdfmake rejeita esse data URL e o download inteiro falha.
        if (!blob.type.startsWith('image/')) {
            throw new Error('Logo não é uma imagem');
        }
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = String(reader.result || '');
                if (!result.startsWith('data:image/')) {
                    reject(new Error('Logo inválido'));
                    return;
                }
                resolve(result);
            };
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(blob);
        });
    }

    private buildHeader(milkControl: MilkControl): any {
        const startDate = milkControl.startedAt
            ? new Date(milkControl.startedAt).toLocaleDateString('pt-BR')
            : 'N/A';

        const producerCode = milkControl.producer?.code || '';

        const tableBody: any[][] = [];
        const tableWidths: any[] = [];
        const row1 = [];

        if (this.logoBase64?.startsWith('data:image/')) {
            tableWidths.push('auto');
            row1.push({
                image: this.logoBase64,
                width: 60,
                margin: [0, 5, 10, 5],
                border: [false, false, true, false]
            });
        }

        tableWidths.push('*', 'auto');
        row1.push(
            {
                stack: [
                    {
                        text: `NOME: ${(milkControl.producer?.name || '').toUpperCase()}`,
                        bold: true,
                        fontSize: 10
                    },
                    {
                        text: `PROPRIEDADE: ${milkControl.property?.name || 'Desconhecida'}`,
                        bold: true,
                        fontSize: 10,
                        margin: [0, 2, 0, 0]
                    }
                ],

                margin: [10, 5, 0, 5],
                border: [false, false, false, false]
            },
            {
                stack: [
                    { text: `Data Controle: ${startDate}`, bold: true, fontSize: 10 },
                    {
                        text: `Controlador: ${milkControl.controller?.name || '—'}`,
                        bold: true,
                        fontSize: 10,
                        margin: [0, 2, 0, 0]
                    },
                    {
                        text: `Rebanho: ${producerCode}`,
                        bold: true,
                        fontSize: 10,
                        margin: [0, 2, 0, 0]
                    }
                ],
                alignment: 'right',
                margin: [0, 5, 0, 5],
                border: [true, false, false, false]
            }
        );

        tableBody.push(row1);

        return {
            table: {
                widths: tableWidths,
                body: tableBody
            },
            layout: {
                hLineWidth: (i: number, node: any) =>
                    i === 0 || i === node.table.body.length ? 1 : 0,
                vLineWidth: (i: number, node: any) =>
                    i === 0 || i === node.table.widths.length ? 1 : 0,
                hLineColor: () => '#aaaaaa',
                vLineColor: () => '#aaaaaa',
                paddingLeft: () => 10,
                paddingRight: () => 10,
                paddingTop: () => 5,
                paddingBottom: () => 5
            },
            margin: [40, 20, 40, 10]
        };
    }

    private buildLotSection(lotData: any): any {
        const { lotName, animals, milkingHeaders } = lotData;
        const totalColumns = milkingHeaders.length + 2;

        const lotTitleRow = [
            {
                text: lotName ? `LOTE ${lotName}` : 'ANIMAIS SEM LOTE',
                style: 'lotBoxTitle',
                colSpan: totalColumns
            },
            ...Array(totalColumns - 1).fill({})
        ];
        const headerRow = ['APELIDO', ...milkingHeaders, 'TOTAL'].map((h) => ({
            text: h,
            style: 'tableHeader'
        }));
        const tableBody: any[] = [lotTitleRow, headerRow];

        animals.forEach((animal: any) => {
            const row: any[] = [{ text: animal.identifier, style: 'centerAlign' }];

            milkingHeaders.forEach((header: string) => {
                const milkingNumber = parseInt(header, 10);
                const prod = animal.productions[milkingNumber] || 0;
                const reason = animal.missingReasons[milkingNumber];

                if (reason) {
                    row.push({ text: reason, style: 'missingReason' });
                } else {
                    const prodText = prod > 0 ? prod.toFixed(2).replace('.', ',') : EMPTY;
                    row.push({ text: prodText, style: 'centerAlign' });
                }
            });

            const totalText = animal.total > 0 ? animal.total.toFixed(2).replace('.', ',') : EMPTY;
            row.push({ text: totalText, bold: true, style: 'centerAlign' });
            tableBody.push(row);
        });

        const summary = this.calculateLotSummary(animals, milkingHeaders);
        if (animals.length > 0) {
            tableBody.push(
                [
                    { text: 'Volume por Ordenha', style: 'summaryRowLabel' },
                    ...summary.columnVolumes.map((v) => ({
                        text: v > 0 ? v.toFixed(2).replace('.', ',') : EMPTY,
                        style: 'summaryRowValue'
                    })),
                    {
                        text:
                            summary.grandTotalVolume > 0
                                ? summary.grandTotalVolume.toFixed(2).replace('.', ',')
                                : EMPTY,
                        style: 'summaryRowValue'
                    }
                ],
                [
                    { text: 'Média por Ordenha', style: 'summaryRowLabel' },
                    ...summary.columnAverages.map((v) => ({
                        text: v > 0 ? v.toFixed(2).replace('.', ',') : EMPTY,
                        style: 'summaryRowValue'
                    })),
                    {
                        text:
                            summary.overallAverage > 0
                                ? summary.overallAverage.toFixed(2).replace('.', ',')
                                : EMPTY,
                        style: 'summaryRowValue'
                    }
                ],
                [
                    { text: 'Total Animais', style: 'summaryRowLabel' },
                    {
                        text: summary.totalUniqueAnimals,
                        colSpan: milkingHeaders.length + 1,
                        style: 'summaryRowValue'
                    },
                    ...Array(milkingHeaders.length).fill({})
                ]
            );
        }

        return [
            {
                layout: {
                    hLineWidth: () => 1,
                    vLineWidth: () => 1,
                    hLineColor: () => '#aaaaaa',
                    vLineColor: () => '#aaaaaa',
                    paddingTop: () => 4,
                    paddingBottom: () => 4
                },
                table: {
                    headerRows: 2,
                    widths: [90, ...Array(milkingHeaders.length + 1).fill('*')],
                    body: tableBody
                },
                margin: [0, 20, 0, 0]
            }
        ];
    }

    private calculateLotSummary(lotAnimals: any[], milkingHeaders: string[]): any {
        const totalUniqueAnimals = lotAnimals.length;
        const columnVolumes = Array(milkingHeaders.length).fill(0);

        lotAnimals.forEach((animal) => {
            milkingHeaders.forEach((header, index) => {
                const milkingNumber = parseInt(header, 10);
                const prod = animal.productions[milkingNumber] || 0;
                columnVolumes[index] += prod;
            });
        });

        const columnAverages = columnVolumes.map((volume, index) => {
            let animalCountForThisMilking = 0;
            lotAnimals.forEach((animal) => {
                const milkingNumber = parseInt(milkingHeaders[index], 10);
                if (
                    !animal.missingReasons[milkingNumber] &&
                    (animal.productions[milkingNumber] || 0)
                ) {
                    animalCountForThisMilking++;
                }
            });
            return animalCountForThisMilking > 0 ? volume / animalCountForThisMilking : 0;
        });

        const grandTotalVolume = lotAnimals.reduce((sum, animal) => sum + animal.total, 0);

        const animalsWithoutMissing = lotAnimals.filter((animal) => {
            return Object.keys(animal.missingReasons).length === 0;
        });

        const overallAverage =
            animalsWithoutMissing.length > 0 ? grandTotalVolume / animalsWithoutMissing.length : 0;

        return {
            columnVolumes,
            columnAverages,
            grandTotalVolume,
            overallAverage,
            totalUniqueAnimals
        };
    }

    private groupAndPrepareData(milkControl: MilkControl): { lots: any[] } {
        const dataByAnimal = new Map<string, any>();
        const distinctMilkingNumbers = new Set<number>();

        (milkControl.milkings || []).forEach((milking, index) => {
            (milking.milkedAnimals || []).forEach((milkedAnimal) => {
                if (!milkedAnimal?.animal) return;
                const animalId = milkedAnimal.animal.id || milkedAnimal.animal.nickname;
                if (!animalId) return;
                const actualMilkingNumber = Number(milkedAnimal.milkingNumber) || milking.milkingNumber || index + 1;
                const rawProduction = milkedAnimal.production as unknown;
                const production =
                    typeof rawProduction === 'string'
                        ? Number(rawProduction.replace(',', '.'))
                        : Number(rawProduction);
                const reason = missingReasonText(milkedAnimal.missingProduction);

                distinctMilkingNumbers.add(actualMilkingNumber);

                if (!dataByAnimal.has(animalId)) {
                    dataByAnimal.set(animalId, {
                        identifier: milkedAnimal.animal.nickname || '—',
                        lot: null,
                        productions: {},
                        missingReasons: {}
                    });
                }
                const animalEntry = dataByAnimal.get(animalId)!;
                if (milkedAnimal.lot) {
                    animalEntry.lot = String(milkedAnimal.lot);
                }

                animalEntry.productions[actualMilkingNumber] = Number.isFinite(production) ? production : 0;

                if (reason) {
                    animalEntry.missingReasons[actualMilkingNumber] = reason;
                }
            });
        });

        const sortedMilkingNumbers = Array.from(distinctMilkingNumbers).sort((a, b) => a - b);
        const globalMilkingHeaders = sortedMilkingNumbers.map((n) => `${n}ª ordenha`);

        const lotsMap = new Map<string | null, any[]>();
        for (const animal of dataByAnimal.values()) {
            const lotKey =
                animal.lot === null ||
                animal.lot === undefined ||
                String(animal.lot).trim() === EMPTY
                    ? null
                    : animal.lot;
            if (!lotsMap.has(lotKey)) {
                lotsMap.set(lotKey, []);
            }
            lotsMap.get(lotKey)!.push(animal);
        }

        const lots = Array.from(lotsMap.entries())
            .map(([lotName, animals]) => {
                animals.forEach((animal) => {
                    animal.total = Object.values<number>(animal.productions).reduce(
                        (sum, p) => sum + (Number(p) || 0),
                        0
                    );
                });

                return {
                    lotName,
                    milkingHeaders: globalMilkingHeaders,
                    animals: animals.sort((a, b) =>
                        String(a.identifier).localeCompare(String(b.identifier), undefined, {
                            numeric: true
                        })
                    )
                };
            })
            .sort((a, b) => {
                if (a.lotName === null) return 1;
                if (b.lotName === null) return -1;
                return a.lotName.localeCompare(b.lotName, undefined, { numeric: true });
            });

        return { lots };
    }

    private buildGeneralSummary(preparedData: any): any {
        if (!preparedData || preparedData.lots.length === 0) return { text: EMPTY };

        const lots = preparedData.lots;

        const lotNames = lots.map((l: any) => (l.lotName ? `LOTE ${l.lotName}` : 'SEM LOTE'));

        const headers = [EMPTY, ...lotNames, 'TOTAL'].map((header) => ({
            text: header,
            style: 'summaryHeader'
        }));

        const totalVolumes = lots.map((lot: any) =>
            lot.animals.reduce((sum: number, a: any) => sum + a.total, 0)
        );

        const totalAnimalsWithProduction = lots.map(
            (lot: any) =>
                lot.animals.filter((animal: any) => Object.keys(animal.missingReasons).length === 0)
                    .length
        );

        const totalAnimalsWithoutProduction = lots.map(
            (lot: any) =>
                lot.animals.filter((animal: any) => Object.keys(animal.missingReasons).length > 0)
                    .length
        );

        const grandTotalVolume = totalVolumes.reduce((sum: number, v: number) => sum + v, 0);

        const grandTotalAnimalsWithProduction = totalAnimalsWithProduction.reduce(
            (sum: number, v: number) => sum + v,
            0
        );

        const grandTotalAnimalsWithoutProduction = totalAnimalsWithoutProduction.reduce(
            (sum: number, v: number) => sum + v,
            0
        );

        const grandAverage =
            grandTotalAnimalsWithProduction > 0
                ? grandTotalVolume / grandTotalAnimalsWithProduction
                : 0;

        const volumeRow = [
            {
                text: 'Total de Produção por Lote',
                style: 'summaryRowLabel'
            },
            ...totalVolumes.map((v) => ({
                text: v.toFixed(2).replace('.', ','),
                style: 'centerAlign'
            })),
            {
                text: grandTotalVolume.toFixed(2).replace('.', ','),
                bold: true,
                style: 'centerAlign'
            }
        ];

        const animalsWithProductionRow = [
            {
                text: 'Total de Animais com Produção',
                style: 'summaryRowLabel'
            },
            ...totalAnimalsWithProduction.map((v) => ({ text: v, style: 'centerAlign' })),
            {
                text: grandTotalAnimalsWithProduction,
                bold: true,
                style: 'centerAlign'
            }
        ];

        const animalsWithoutProductionRow = [
            {
                text: 'Total de Animais sem Produção',
                style: 'summaryRowLabel'
            },
            ...totalAnimalsWithoutProduction.map((v) => ({ text: v, style: 'centerAlign' })),
            {
                text: grandTotalAnimalsWithoutProduction,
                bold: true,
                style: 'centerAlign'
            }
        ];

        const averageRow = [
            { text: 'Média por Lote', style: 'summaryRowLabel' },
            ...lots.map((lot: any) => {
                const total = lot.animals.reduce((sum: number, a: any) => sum + a.total, 0);
                const animalsWithProduction = lot.animals.filter(
                    (animal: any) => Object.keys(animal.missingReasons).length === 0
                );
                const count = animalsWithProduction.length > 0 ? animalsWithProduction.length : 1;
                return { text: (total / count).toFixed(2).replace('.', ','), style: 'centerAlign' };
            }),
            { text: grandAverage.toFixed(2).replace('.', ','), bold: true, style: 'centerAlign' }
        ];

        return {
            pageBreak: 'before',
            text: 'Dados Gerais',
            style: 'lotTitle',
            table: {
                headerRows: 1,
                widths: ['auto', ...Array(lotNames.length).fill('*'), 'auto'],
                body: [
                    headers,
                    volumeRow,
                    animalsWithProductionRow,
                    animalsWithoutProductionRow,
                    averageRow
                ]
            },
            layout: 'lightHorizontalLines'
        };
    }
}
